import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { AddControlParentsMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

const mapTrpcResponseToGraphQL = (
  affectedRows: number
): AddControlParentsMutation => {
  return {
    insert_control_parent: {
      __typename: 'control_parent_mutation_response',
      affected_rows: affectedRows,
    },
  };
};

export const useCreateControlParentsTRPC = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();

  const mutation = useMutation({
    ...trpc.frontend.control.createParents.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.control.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.control.controlsBasic.queryKey(),
        });
      },
    }),
    throwOnError: true,
  });

  useEffect(() => {
    if (trpcEnabled && mutation.error) {
      addNotification({
        type: 'error',
        content: mutation.error.message,
      });
    }
  }, [mutation.error, addNotification, trpcEnabled]);

  return {
    createControlParents: async (
      objects: { ControlId: string; ParentId: string }[]
    ): Promise<AddControlParentsMutation> => {
      const result = await mutation.mutateAsync({ objects });

      return mapTrpcResponseToGraphQL(result.affected_rows);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
